import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { BarChart3, Download, Calculator, Wallet, Upload, AlertTriangle, CheckCircle } from 'lucide-react';

// Import budget components
import BudgetDashboard from './BudgetDashboard';
import { 
  BudgetOverviewCards, 
  ExpensesTable, 
  ProjectBudgetsTab, 
  PendingApprovalsTab,
  ExpenseDialog,
  EditExpenseDialog,
  ExportExpensesDialog,
  BatchExpenseDialog,
  ApprovalDialog,
  TaxCalculator,
  ReimbursementDashboard
} from './budget';
import { useDisplayCurrency, SYMBOL } from '../hooks/useDisplayCurrency';
import usePermissions from '../hooks/usePermissions';
import useSubTabShortcut from '../hooks/useSubTabShortcut';
import { MileageSheets } from './mileage/MileageSheets';
import { MileageTemplateSettings } from './mileage/MileageTemplateSettings';
import { Car, Landmark } from 'lucide-react';
import { BankReconciliation } from './bank/BankReconciliation';
import useRememberedInputs from '../hooks/useRememberedInputs';

// Import custom hooks
import { 
  useExpenses, 
  useBudgetSummaries, 
  useExpenseOperations, 
  useBatchExpenseOperations,
  useReceiptOperations 
} from '../hooks/useBudget';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Component to fetch and display receipts with availability for re-upload
 */
const ReuploadReceiptsList = ({ expense, onReupload, onClose }) => {
  const { t } = useTranslation();
  const [receipts, setReceipts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await axios.get(`${API}/expenses/${expense.id}/receipts`);
        setReceipts(response.data.receipts || []);
      } catch (err) {
        console.error('Failed to fetch receipts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReceipts();
  }, [expense.id]);

  if (loading) {
    return <p className="text-sm text-gray-500 py-4 text-center">{t('budget.loadingReceipts')}</p>;
  }

  const unavailable = receipts.filter(r => r.available === false);
  const available = receipts.filter(r => r.available !== false);

  return (
    <div className="space-y-3 py-2">
      {unavailable.length === 0 && (
        <p className="text-sm text-green-700 py-2">{t('budget.allReceiptsAreAvailableNoRe')}</p>
      )}
      {unavailable.map((receipt) => (
        <div key={receipt.id} className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{receipt.original_name || receipt.filename}</p>
            <p className="text-xs text-orange-600">{t('budget.fileUnavailable')}</p>
          </div>
          <label className="cursor-pointer">
            <input
              type="file"
              className="hidden"
              accept="image/*,.pdf"
              data-testid={`reupload-input-${receipt.id}`}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const result = await onReupload(expense.id, receipt.id, file);
                if (result.success) {
                  onClose();
                }
              }}
            />
            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors">
              <Upload className="h-3.5 w-3.5" />
              {t('budget.upload')}
            </span>
          </label>
        </div>
      ))}
      {available.length > 0 && (
        <div className="mt-2">
          <p className="text-sm text-green-700 flex items-center gap-1">
            <CheckCircle className="h-4 w-4" />
            {available.length} receipt(s) are available
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Helper to get display name for expense category
 */
const getCategoryDisplayName = (expenseCategories, categoryKey) => {
  const cat = expenseCategories[categoryKey];
  if (!cat) return categoryKey;
  if (Array.isArray(cat)) return categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
  return cat.display_name || categoryKey;
};

/**
 * Helper to format currency values
 */
const formatCurrency = (amount, currency = 'PLN') => {
  const cur = currency || 'PLN';
  const formatted = new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount || 0);
  if (cur === 'PLN') return `${formatted} zł`;
  return `${SYMBOL[cur] || ''}${formatted}`;
};

/**
 * Status badge component
 */
const getStatusBadge = (status) => {
  const statusConfig = {
    draft: { color: 'bg-gray-500', label: i18n.t('budget.draft') },
    submitted: { color: 'bg-blue-500', label: i18n.t('budget.submitted') },
    manager_approved: { color: 'bg-yellow-500', label: i18n.t('budget.managerApproved') },
    admin_approved: { color: 'bg-green-500', label: i18n.t('budget.adminApproved') },
    rejected: { color: 'bg-red-500', label: i18n.t('budget.rejected') },
    paid: { color: 'bg-purple-500', label: i18n.t('budget.paid') }
  };
  const config = statusConfig[status] || { color: 'bg-gray-500', label: status };
  return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
};

/**
 * Default expense categories
 */
const DEFAULT_EXPENSE_CATEGORIES = {
  travel: { display_name: 'Travel Expenses', visible: true, subcategories: ['Flight Tickets', 'Bus Fare', 'Taxi Fare', 'Ferry Fare', 'Fuel', 'Mileage'] },
  accommodation: { display_name: 'Accommodation', visible: true, subcategories: ['Hotel', 'Apartment Rental', 'Hostel'] },
  tools: { display_name: 'Tools', visible: true, subcategories: ['Hand Tools', 'Power Tools', 'Safety Equipment'] },
  consumables: { display_name: 'Consumables', visible: true, subcategories: ['Materials', 'Supplies', 'Office Supplies'] },
  meals: { display_name: 'Meals', visible: true, subcategories: ['Breakfast', 'Lunch', 'Dinner', 'Per Diem'] },
  other: { display_name: 'Other', visible: true, subcategories: ['Communication', 'Insurance', 'Permits/Licenses', 'Miscellaneous'] }
};

/**
 * Initial expense form state
 */
const getInitialExpenseForm = (currentEmployeeId = '') => ({
  project_id: '',
  expense_type: 'project_cost',
  category: 'travel',
  subcategory: '',
  amount: '',
  currency: 'EUR',
  expense_date: new Date().toISOString().split('T')[0],
  description: '',
  vendor: '',
  invoice_number: '',
  assigned_employee_id: currentEmployeeId,
  payment_method: ''
});

/**
 * BudgetTab Component - Manages budget, expenses, and financial reporting
 */
const BUDGET_SUBTAB_LABELS = {
  dashboard: 'budget.dashboard', expenses: 'budget.expenses', budgets: 'budget.projectBudgets',
  approvals: 'budget.pendingApprovals', reports: 'budget.reports', reimbursements: 'budget.reimbursements',
  calculate: 'budget.calculate', mileage: 'mileage.tab',
};

const BudgetTab = ({ projects, employees, settings, currentUser, navContext }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  // Matches the backend's expenses.view_all gate.
  const isAdminOrManager = can('expenses.view_all');
  
  // Filter state for expenses
  const [selectedProject, setSelectedProject] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assetFilter, setAssetFilter] = useState(null);
  const [budgetSubTab, setBudgetSubTab] = useState(isAdminOrManager ? "dashboard" : "expenses");
  useSubTabShortcut({
    tab: 'budget', ctxKey: 'budgetSubTab', value: budgetSubTab, setValue: setBudgetSubTab,
    labels: BUDGET_SUBTAB_LABELS,
    allowed: isAdminOrManager ? Object.keys(BUDGET_SUBTAB_LABELS) : ['expenses', 'calculate', 'mileage'],
  });

  // Handle cross-module navigation context
  React.useEffect(() => {
    if (navContext?.filterAssetId) {
      setAssetFilter({ id: navContext.filterAssetId, name: navContext.filterAssetName || navContext.filterAssetId });
      setSelectedProject('all');
      setStatusFilter('all');
      setDateFrom('');
      setDateTo('');
      setEmployeeFilter('all');
      setCategoryFilter('all');
      setAssetLinkActive(true);
      setBudgetSubTab("expenses");
    }
  }, [navContext]);
  
  // Budget date range filter (for dashboard and project budgets)
  // Default to current year
  const currentYear = new Date().getFullYear();
  const [budgetDateRange, setBudgetDateRange] = useState({
    startDate: `${currentYear}-01-01`,
    endDate: `${currentYear}-12-31`
  });
  
  // Sorting for project budgets
  const [projectSortBy, setProjectSortBy] = useState('start_date');
  const [projectSortOrder, setProjectSortOrder] = useState('desc');
  const { setCurrency: setDisplayCurrency } = useDisplayCurrency();
  // "Reset to my defaults" on Dashboard / Project Budgets: the app's standard view.
  const resetBudgetViewFilters = () => {
    setBudgetDateRange({ startDate: `${currentYear}-01-01`, endDate: `${currentYear}-12-31` });
    setProjectSortBy('start_date');
    setProjectSortOrder('desc');
    setDisplayCurrency('EUR');
    axios.put(`${API}/me/ui-state/budget-project-status`, { value: { statusFilter: 'active' } }).catch(() => {});
  };
  // Arriving from an asset link: show that asset's expenses, not the remembered filters.
  const viaAssetLink = !!navContext?.filterAssetId;
  const [assetLinkActive, setAssetLinkActive] = useState(viaAssetLink);
  useRememberedInputs('budget-expense-filters', {
    selectedProject: [selectedProject, setSelectedProject], statusFilter: [statusFilter, setStatusFilter],
    dateFrom: [dateFrom, setDateFrom], dateTo: [dateTo, setDateTo],
    employeeFilter: [employeeFilter, setEmployeeFilter], categoryFilter: [categoryFilter, setCategoryFilter],
  }, { skip: viaAssetLink, pause: assetLinkActive || !!assetFilter });
  useRememberedInputs('budget-project-budgets', {
    dateRange: [budgetDateRange, setBudgetDateRange],
    sortBy: [projectSortBy, setProjectSortBy], sortOrder: [projectSortOrder, setProjectSortOrder],
  });
  
  // Dialog state
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [approvalAction, setApprovalAction] = useState('');
  const [reuploadExpense, setReuploadExpense] = useState(null);
  
  // Get current employee ID
  const currentEmployeeId = useMemo(() => {
    const emp = employees?.find(e => e.user_id === currentUser?.id);
    return emp?.id || '';
  }, [employees, currentUser]);
  
  // Form state
  const [newExpense, setNewExpense] = useState(getInitialExpenseForm(currentEmployeeId));
  const [receiptFiles, setReceiptFiles] = useState([]);
  
  // Assigned projects state (for expense creation - only projects user can add expenses to)
  const [assignedProjects, setAssignedProjects] = useState([]);

  // Computed values
  const expenseCategories = settings?.expense_categories || DEFAULT_EXPENSE_CATEGORIES;
  const currencies = settings?.currencies || ['EUR', 'PLN', 'GBP', 'USD'];
  
  // Update newExpense when currentEmployeeId changes
  React.useEffect(() => {
    if (currentEmployeeId) {
      setNewExpense(prev => ({ ...prev, assigned_employee_id: currentEmployeeId }));
    }
  }, [currentEmployeeId]);

  // Use custom hooks for data fetching
  const { expenses, loading, refetch: refetchExpenses } = useExpenses(selectedProject, statusFilter, dateFrom, dateTo, employeeFilter, categoryFilter);
  const { budgetSummaries, budgetLoading, loadingProgress, refetch: refetchBudgets } = useBudgetSummaries(projects, isAdminOrManager, budgetDateRange);

  // Use custom hooks for operations
  const { createExpense, submitExpense, deleteExpense, approveExpense, changeExpenseStatus } = 
    useExpenseOperations(refetchExpenses, refetchBudgets);
  const { createBatchExpenses } = useBatchExpenseOperations(refetchExpenses);
  const { viewReceipt, viewAllReceipts, downloadAllReceipts, reuploadReceipt } = useReceiptOperations(refetchExpenses);
  
  // Fetch assigned projects for expense creation
  React.useEffect(() => {
    const fetchAssignedProjects = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        const response = await fetch(`${API}/projects/my-assigned`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAssignedProjects(data);
        }
      } catch (error) {
        console.error('Error fetching assigned projects:', error);
        // Fallback to all projects for admins/managers
        if (isAdminOrManager) {
          setAssignedProjects(projects);
        }
      }
    };
    
    fetchAssignedProjects();
  }, [projects, isAdminOrManager]);

  // Fetch inventory assets for linking
  const [inventoryAssets, setInventoryAssets] = useState([]);
  React.useEffect(() => {
    const fetchAssets = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        const r = await axios.get(`${API}/inventory/assets/search-for-linking`, { params: { q: '' } });
        setInventoryAssets(r.data);
      } catch {}
    };
    fetchAssets();
  }, []);

  // Memoized category display name getter
  const getCategoryName = useCallback(
    (categoryKey) => getCategoryDisplayName(expenseCategories, categoryKey),
    [expenseCategories]
  );

  // Memoized project name getter
  const getProjectName = useCallback(
    (projectId) => {
      const project = projects.find(p => p.id === projectId);
      return project?.name || projectId;
    },
    [projects]
  );

  // Display currency drives which set of fields we sum: backend
  // returns both `*_pln` (canonical) and `*_display` (re-expressed in
  // the chosen display currency). When the response is old/cached
  // and lacks `*_display`, we fall back to PLN unchanged.
  const { currency: displayCurrency } = useDisplayCurrency();
  const pickDisp = (s, key) =>
    (s && (s[`${key}_display`] != null ? s[`${key}_display`] : s[`${key}_pln`])) || 0;

  // Calculate totals
  const totals = useMemo(() => ({
    budget: Object.values(budgetSummaries).reduce((sum, s) => sum + (s.budget_amount || 0), 0),
    revenue: Object.values(budgetSummaries).reduce((sum, s) => sum + pickDisp(s, 'revenue'), 0),
    costs: Object.values(budgetSummaries).reduce((sum, s) => sum + pickDisp(s, 'total_costs'), 0),
    profit: Object.values(budgetSummaries).reduce((sum, s) => sum + pickDisp(s, 'profit'), 0)
  }), [budgetSummaries, displayCurrency]); // eslint-disable-line

  // Pending approvals count
  const pendingApprovalsCount = useMemo(
    () => expenses.filter(e => ['submitted', 'manager_approved'].includes(e.status)).length,
    [expenses]
  );

  // Reset expense form
  const resetExpenseForm = useCallback(() => {
    setNewExpense(getInitialExpenseForm());
    setReceiptFiles([]);
  }, []);

  // Handle create expense
  const handleCreateExpense = async () => {
    if (!newExpense.project_id || !newExpense.amount || !newExpense.subcategory) {
      toast.error(t('budget.pleaseFillInAllRequiredFields'));
      return;
    }

    const result = await createExpense(newExpense, receiptFiles);
    if (result.success) {
      setExpenseDialogOpen(false);
      resetExpenseForm();
    }
  };

  // Handle view receipt - opens all receipts in new tabs
  const handleViewReceipt = (expense) => {
    viewAllReceipts(expense);
  };

  // Handle download all receipts as ZIP
  const handleDownloadReceipts = (expense) => {
    downloadAllReceipts(expense.id);
  };

  // Handle re-upload receipt for lost files
  const handleReuploadReceipt = (expense) => {
    setReuploadExpense(expense);
  };

  // Handle submit expense
  const handleSubmitExpense = async (expenseId) => {
    await submitExpense(expenseId);
  };

  // Handle delete expense
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm(t('budget.areYouSureYouWantTo'))) return;
    await deleteExpense(expenseId);
  };

  // Handle edit expense
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setEditDialogOpen(true);
  };

  // Handle copy expense — pre-fill the new-expense dialog with the
  // chosen expense's fields. Receipts are NOT copied (each expense
  // has its own evidence). Status/approval/audit fields are reset.
  const handleCopyExpense = (expense) => {
    if (!expense) return;
    setNewExpense({
      project_id: expense.project_id || '',
      category: expense.category || 'travel',
      subcategory: expense.subcategory || '',
      amount: expense.amount?.toString() || '',
      currency: expense.currency || 'EUR',
      // Default the date to today — copying old expenses with the
      // original date is rarely what the user wants.
      expense_date: new Date().toISOString().split('T')[0],
      description: expense.description ? `${expense.description} (copy)` : '',
      vendor: expense.vendor || '',
      invoice_number: '',  // unique per real expense — never carry over
      payment_method: expense.payment_method || '',
      assigned_employee_id: expense.assigned_employee_id || currentEmployeeId || '',
      billable_to_customer: expense.billable_to_customer || false,
      asset_id: expense.asset_id || '',
    });
    setReceiptFiles([]);
    setExpenseDialogOpen(true);
    toast.info(t('budget.copiedExpenseReviewAndSubmit'));
  };

  // Handle edit save (refetch expenses after save)
  const handleEditSave = () => {
    refetchExpenses();
    refetchBudgets?.();
  };

  // Handle approval
  const handleApproval = async (expenseId, action, comments) => {
    await approveExpense(expenseId, action, comments);
  };

  // Handle status change
  const handleChangeStatus = async (expenseId, action, comment = '') => {
    await changeExpenseStatus(expenseId, action, comment);
  };

  // Handle bulk actions on multiple expenses
  const handleBulkAction = async (expenseIds, action, comment = '') => {
    if (!expenseIds || expenseIds.length === 0) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const expenseId of expenseIds) {
      try {
        if (action === 'delete') {
          await deleteExpense(expenseId);
        } else {
          await changeExpenseStatus(expenseId, action, comment);
        }
        successCount++;
      } catch (err) {
        console.error(`Failed to ${action} expense ${expenseId}:`, err);
        failCount++;
      }
    }
    
    if (failCount === 0) {
      toast.success(t(`budget.bulkDone.${['delete', 'approve', 'reject', 'mark_paid'].includes(action) ? action : 'other'}`, { count: successCount }));
    } else {
      toast.warning(t('budget.bulkPartial', { ok: successCount, failed: failCount }));
    }
    
    refetchExpenses();
    refetchBudgets();
  };

  // Handle batch submit
  const handleBatchSubmit = async (batchExpenses, submitImmediately) => {
    const invalidExpenses = batchExpenses.filter(exp => 
      !exp.project_id || !exp.amount || !exp.subcategory
    );
    
    if (invalidExpenses.length > 0) {
      toast.error(t('budget.pleaseFillInAllRequiredFields2'));
      return { success: false };
    }

    if (batchExpenses.length === 0) {
      toast.error(t('budget.pleaseAddAtLeastOneExpense'));
      return { success: false };
    }

    return await createBatchExpenses(batchExpenses, submitImmediately);
  };

  // Handle approve expense click
  const handleApproveExpenseClick = (expense) => {
    setSelectedExpense(expense);
    setApprovalAction('approve');
    setApprovalDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards - Admin/Manager Only */}
      {isAdminOrManager && (
        <BudgetOverviewCards
          totalRevenue={totals.revenue}
          totalCosts={totals.costs}
          totalProfit={totals.profit}
          pendingApprovalsCount={pendingApprovalsCount}
          formatCurrency={(v) => formatCurrency(v, displayCurrency)}
        />
      )}

      <Tabs value={budgetSubTab} onValueChange={setBudgetSubTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          {isAdminOrManager && (
            <TabsTrigger value="dashboard" className="flex items-center gap-2 px-3 py-2" data-testid="budget-dashboard-tab">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">{t('budget.dashboard')}</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="expenses" className="px-3 py-2" data-testid="tab-expenses">
            <span className="hidden sm:inline">{t('budget.expenses')}</span>
            <span className="sm:hidden">{t('budget.exp')}</span>
          </TabsTrigger>
          {isAdminOrManager && (
            <TabsTrigger value="budgets" className="px-3 py-2" data-testid="tab-budgets">
              <span className="hidden sm:inline">{t('budget.projectBudgets')}</span>
              <span className="sm:hidden">{t('budget.budgets')}</span>
            </TabsTrigger>
          )}
          {isAdminOrManager && (
            <TabsTrigger value="approvals" className="px-3 py-2" data-testid="tab-approvals">
              <span className="hidden sm:inline">{t('budget.pendingApprovals')}</span>
              <span className="sm:hidden">{t('budget.approvals')}</span>
            </TabsTrigger>
          )}
          {isAdminOrManager && (
            <TabsTrigger value="reports" className="flex items-center gap-2 px-3 py-2" data-testid="tab-budget-reports">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">{t('budget.reports')}</span>
            </TabsTrigger>
          )}
          {isAdminOrManager && (
            <TabsTrigger value="reimbursements" className="flex items-center gap-2 px-3 py-2" data-testid="reimbursements-tab">
              <Wallet className="h-4 w-4" />
              <span className="hidden sm:inline">{t('budget.reimbursements')}</span>
              <span className="sm:hidden">{t('budget.reimb')}</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="mileage" className="flex items-center gap-2 px-3 py-2" data-testid="tab-mileage-sheets">
            <Car className="h-4 w-4" />
            <span className="hidden sm:inline">{t('mileage.tab')}</span>
          </TabsTrigger>
          {can('budget.edit') && (
            <TabsTrigger value="bank" className="flex items-center gap-2 px-3 py-2" data-testid="tab-bank">
              <Landmark className="h-4 w-4" />
              <span className="hidden sm:inline">{t('bank.tab')}</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="calculate" className="flex items-center gap-2 px-3 py-2" data-testid="tab-calculate">
            <Calculator className="h-4 w-4" />
            <span className="hidden sm:inline">{t('budget.calculate')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        {isAdminOrManager && (
          <TabsContent value="dashboard" className="space-y-4">
            {budgetLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">
                    {t('budget.loadingBudgetData')}
                    {loadingProgress.total > 0 && (
                      <span className="block text-sm mt-1">
                        Fetching {loadingProgress.total} projects in parallel
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <BudgetDashboard 
                budgetSummaries={budgetSummaries}
                projects={projects}
                expenses={expenses}
                dateRange={budgetDateRange}
                onDateRangeChange={setBudgetDateRange}
                onResetFilters={resetBudgetViewFilters}
              />
            )}
          </TabsContent>
        )}

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <ExpensesTable
            expenses={assetFilter ? expenses.filter(e => e.asset_id === assetFilter.id) : expenses}
            projects={projects}
            employees={employees}
            expenseCategories={expenseCategories}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            employeeFilter={employeeFilter}
            setEmployeeFilter={setEmployeeFilter}
            isAdminOrManager={isAdminOrManager}
            formatCurrency={formatCurrency}
            getProjectName={getProjectName}
            getStatusBadge={getStatusBadge}
            onAddExpense={() => setExpenseDialogOpen(true)}
            onBatchEntry={() => setBatchDialogOpen(true)}
            onViewReceipt={handleViewReceipt}
            onDownloadReceipts={handleDownloadReceipts}
            onEditExpense={handleEditExpense}
            onCopyExpense={handleCopyExpense}
            onSubmitExpense={handleSubmitExpense}
            onDeleteExpense={handleDeleteExpense}
            onApproveExpense={handleApproveExpenseClick}
            onChangeStatus={handleChangeStatus}
            onBulkAction={handleBulkAction}
            onExport={() => setExportDialogOpen(true)}
            onReuploadReceipt={handleReuploadReceipt}
            assetFilter={assetFilter}
            onClearAssetFilter={() => { setAssetFilter(null); setAssetLinkActive(false); }}
            inventoryAssets={inventoryAssets}
            onSetAssetFilter={setAssetFilter}
          />
        </TabsContent>

        {/* Project Budgets Tab */}
        {isAdminOrManager && (
          <TabsContent value="budgets" className="space-y-4">
            <ProjectBudgetsTab
              projects={projects}
              budgetSummaries={budgetSummaries}
              budgetLoading={budgetLoading}
              formatCurrency={formatCurrency}
              dateRange={budgetDateRange}
              onDateRangeChange={setBudgetDateRange}
              sortBy={projectSortBy}
              sortOrder={projectSortOrder}
              onSortChange={(sortBy, sortOrder) => {
                setProjectSortBy(sortBy);
                setProjectSortOrder(sortOrder);
              }}
              onResetFilters={resetBudgetViewFilters}
            />
          </TabsContent>
        )}

        {/* Pending Approvals Tab */}
        {isAdminOrManager && (
          <TabsContent value="approvals" className="space-y-4">
            <PendingApprovalsTab
              expenses={expenses}
              formatCurrency={formatCurrency}
              getProjectName={getProjectName}
              getStatusBadge={getStatusBadge}
              onApprove={(expense) => {
                setSelectedExpense(expense);
                setApprovalAction('approve');
                setApprovalDialogOpen(true);
              }}
              onReject={(expense) => {
                setSelectedExpense(expense);
                setApprovalAction('reject');
                setApprovalDialogOpen(true);
              }}
              onExport={() => setExportDialogOpen(true)}
            />
          </TabsContent>
        )}

        {/* Reports Tab */}
        {isAdminOrManager && (
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('budget.expenseReports')}</CardTitle>
                <CardDescription>
                  {t('budget.exportExpensesToExcelForAccounting')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-6 bg-gray-50 rounded-lg">
                  <Download className="h-12 w-12 text-primary" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{t('budget.monthlyExpenseExport')}</h3>
                    <p className="text-gray-600 text-sm">
                      {t('budget.generateExcelReportsFilteredByTime')}
                    </p>
                  </div>
                  <Button onClick={() => setExportDialogOpen(true)}>
                    <Download className="h-4 w-4 mr-2" /> {t('budget.exportExpenses')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Reimbursements Tab */}
        {isAdminOrManager && (
          <TabsContent value="reimbursements" className="space-y-4">
            <ReimbursementDashboard />
          </TabsContent>
        )}

        {/* Calculate Tab */}
        <TabsContent value="mileage" className="space-y-4">
          <MileageSheets />
          {isAdminOrManager && currentUser?.role !== 'manager' && <MileageTemplateSettings />}
        </TabsContent>
        {can('budget.edit') && (
          <TabsContent value="bank" className="space-y-4">
            <BankReconciliation />
          </TabsContent>
        )}
        <TabsContent value="calculate" className="space-y-4">
          <TaxCalculator />
        </TabsContent>
      </Tabs>

      {/* Add Expense Dialog */}
      <ExpenseDialog
        open={expenseDialogOpen}
        onOpenChange={setExpenseDialogOpen}
        expense={newExpense}
        setExpense={setNewExpense}
        projects={assignedProjects}
        employees={employees}
        currentEmployeeId={currentEmployeeId}
        isAdminOrManager={isAdminOrManager}
        expenseCategories={expenseCategories}
        currencies={currencies}
        paymentMethods={settings?.payment_methods || ['Own Payment', 'Company Card', 'Cash Advance', 'Bank Transfer']}
        receiptFiles={receiptFiles}
        setReceiptFiles={setReceiptFiles}
        onSubmit={handleCreateExpense}
        onCancel={() => {
          setExpenseDialogOpen(false);
          resetExpenseForm();
        }}
        assets={inventoryAssets}
      />

      {/* Edit Expense Dialog */}
      <EditExpenseDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        expense={editingExpense}
        projects={assignedProjects}
        employees={employees}
        currentEmployeeId={currentEmployeeId}
        isAdminOrManager={isAdminOrManager}
        expenseCategories={expenseCategories}
        currencies={currencies}
        paymentMethods={settings?.payment_methods || ['Own Payment', 'Company Card', 'Cash Advance', 'Bank Transfer']}
        onSave={handleEditSave}
        assets={inventoryAssets}
      />

      {/* Batch Expense Dialog */}
      <BatchExpenseDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        projects={assignedProjects}
        expenseCategories={expenseCategories}
        currencies={currencies}
        getCategoryDisplayName={getCategoryName}
        onSubmit={handleBatchSubmit}
      />

      {/* Export Expenses Dialog */}
      <ExportExpensesDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
      />

      {/* Approval Dialog */}
      <ApprovalDialog
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        expense={selectedExpense}
        action={approvalAction}
        formatCurrency={formatCurrency}
        onApprove={handleApproval}
      />

      {/* Re-upload Receipt Dialog */}
      <Dialog open={!!reuploadExpense} onOpenChange={(open) => !open && setReuploadExpense(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {t('budget.reUploadLostReceipts')}
            </DialogTitle>
            <DialogDescription>
              {t('budget.someReceiptFilesAreNotIn')}
            </DialogDescription>
          </DialogHeader>
          {reuploadExpense && (
            <ReuploadReceiptsList
              expense={reuploadExpense}
              onReupload={reuploadReceipt}
              onClose={() => setReuploadExpense(null)}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReuploadExpense(null)} data-testid="dialog-close-btn">{t('budget.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BudgetTab;
